import * as Phaser from "phaser"
import { THEME } from "../core/theme"
import { GAME_HEIGHT, GAME_WIDTH } from "../data/constants"
import type { GameData, GameSprite, SectorModifier } from "../types"
import type { EnemySystem } from "./enemy-system"
import type { VfxManager } from "./vfx-manager"

const COLOR = THEME.colors
const AMBUSH_DIFFICULTY_STEP = 0.12

interface SectorEffectsOptions {
  scene: Phaser.Scene
  enemies: EnemySystem
  vfx: VfxManager
  getGameData: () => GameData
  getDogSprite: () => GameSprite
  getSectorModifier: () => SectorModifier | undefined
  getWaveNumber: () => number
  getDarknessOverlay: () => Phaser.GameObjects.Graphics | undefined
  updateDarknessOverlay: (alpha: number) => void
}

export class SectorEffects {
  private readonly options: SectorEffectsOptions
  private ambushCooldown = 0
  private windTimer = 0
  private windGustActive = false
  private windGustTimer = 0

  constructor(options: SectorEffectsOptions) {
    this.options = options
  }

  public reset(): void {
    this.ambushCooldown = 0
    this.windTimer = 0
    this.windGustActive = false
    this.windGustTimer = 0
  }

  private getAmbushDifficultyMultiplier(): number {
    const waveNumber = this.options.getWaveNumber()
    return 1 + (waveNumber - 1) * AMBUSH_DIFFICULTY_STEP
  }

  public update(delta: number): void {
    const sectorModifier = this.options.getSectorModifier()
    if (!sectorModifier) return

    if (sectorModifier.id === "snow_wind") {
      const gustInterval = sectorModifier.params.gustInterval ?? 4000
      const gustDuration = sectorModifier.params.gustDuration ?? 1500
      const windForce = sectorModifier.params.windForce ?? 40

      if (this.windGustActive) {
        this.windGustTimer -= delta
        if (this.windGustTimer <= 0) {
          this.windGustActive = false
          this.windTimer = 0
        } else {
          const body = this.options.getDogSprite()
            .body as Phaser.Physics.Arcade.Body
          if (body) {
            const windDir =
              Math.sin(this.options.scene.time.now / 500) > 0 ? 1 : -1
            body.setVelocityY(
              body.velocity.y + windDir * windForce * (delta / 1000),
            )
          }
        }
      } else {
        this.windTimer += delta
        if (this.windTimer >= gustInterval) {
          this.windGustActive = true
          this.windGustTimer = gustDuration
        }
      }
    }

    if (
      sectorModifier.type === "visibility" &&
      this.options.getDarknessOverlay()
    ) {
      const alpha = sectorModifier.params.darknessAlpha ?? 0.35
      this.options.updateDarknessOverlay(alpha)
    }

    if (sectorModifier.id === "fields_greed") {
      const ambushChance = sectorModifier.params.ambushChance ?? 0.15
      this.ambushCooldown -= delta
      if (this.ambushCooldown <= 0) {
        this.ambushCooldown = 3000
        if (
          Math.random() < ambushChance &&
          this.options.getGameData().state === "playing"
        ) {
          this.options.vfx.flashScreen(COLOR.danger, 0.15, 300)
          this.options.scene.time.delayedCall(500, () => {
            if (this.options.getGameData().state !== "playing") return
            const dogSprite = this.options.getDogSprite()
            const laneYs = [
              GAME_HEIGHT * 0.24,
              GAME_HEIGHT * 0.48,
              GAME_HEIGHT * 0.74,
            ]
            const spawnY =
              laneYs.reduce((closest, lane) => {
                return Math.abs(lane - dogSprite.y) <
                  Math.abs(closest - dogSprite.y)
                  ? lane
                  : closest
              }, laneYs[1]) +
              (Math.random() - 0.5) * 24
            const waveNumber = this.options.getWaveNumber()
            const difficultyMultiplier = this.getAmbushDifficultyMultiplier()
            const enemy = this.options.enemies.createEnemyByType(
              "kamikaze",
              GAME_WIDTH + 80,
              Phaser.Math.Clamp(spawnY, 50, GAME_HEIGHT - 50),
              waveNumber,
              difficultyMultiplier,
            )
            this.options.enemies.spawnEnemy(enemy)
          })
        }
      }
    }
  }
}
